<template>
  <div class="login-form p-6 flex flex-col h-full">
    <div class="text-center mb-8">
      <div class="logo-container mx-auto w-16 h-16 mb-4">
        <img 
          src="/orbitar_chat_logo.png" 
          alt="Orbitar AI"
          class="h-16 w-16 rounded-full object-cover"
        />
      </div>
      <h1 class="text-2xl font-semibold text-gray-800">Login to Chat</h1>
      <p class="text-gray-600 mt-2">Please sign in to continue</p>
    </div>
    
    <form @submit.prevent="handleSubmit" class="flex-1 flex flex-col">
      <div class="mb-4">
        <label for="username" class="block text-sm font-medium text-gray-700 mb-1">Username</label>
        <input 
          id="username"
          v-model="username" 
          type="text" 
          required
          :disabled="loading"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          :class="{ 'opacity-70': loading }"
        />
      </div>
      
      <div class="mb-6">
        <label for="password" class="block text-sm font-medium text-gray-700 mb-1">Password</label>
        <input 
          id="password"
          v-model="password" 
          type="password" 
          required
          :disabled="loading"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          :class="{ 'opacity-70': loading }"
        />
      </div>
      
      <div class="error-message mb-4 text-sm text-red-600" v-if="errorMessage">
        {{ errorMessage }}
      </div>
      
      <div class="mt-auto mobile-button-container">
        <button 
          type="submit" 
          class="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-primary hover:bg-opacity-90 focus:outline-none mobile-submit-button"
          :disabled="loading || !canSubmit"
          :class="{ 'opacity-70 cursor-not-allowed': loading || !canSubmit }"
        >
          <span v-if="loading">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Signing in...
          </span>
          <span v-else>Sign in</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const emit = defineEmits<{
  (event: 'login', credentials: { username: string, password: string }): void;
}>();

// Form state
const username = ref('');
const password = ref('');
const loading = ref(false);
const errorMessage = ref('');

// Computed properties
const canSubmit = computed(() => 
  username.value.trim().length > 0 && 
  password.value.trim().length > 0
);

// Methods
const handleSubmit = async () => {
  if (!canSubmit.value || loading.value) return;
  
  loading.value = true;
  errorMessage.value = '';
  
  try {
    // Emit login event with credentials
    emit('login', {
      username: username.value,
      password: password.value
    });
    
    // Note: In a real implementation, the parent component would
    // handle success/failure and this component would react accordingly
  } catch (error) {
    errorMessage.value = 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* Component-specific styles can go here */
</style> 